import {
    AddOperation, BoxGeometry, Geometry, Line, LineBasicMaterial, Mesh, MeshLambertMaterial, MixOperation, Plane,
    Quaternion,
    Vector3
} from "three"
import { Promise } from "es6-promise-polyfill"
import callback from "./util/callback";
import Stats from "./util/stats";
import Markers from "./util/markers";
import Cannon from "cannon"

// window.THREE = THREE;

const THRUST_MAT_INDEX = 3;

let geometry, collisionGeom, materials;

const THRUST = 0.7;

const TAU = Math.PI * 2;
const min = Math.min;
const max = Math.max;
const round = Math.round;
const abs = Math.abs;
const atan2 = Math.atan2;

const MAX_SLERP = 0.5; // 22.5 degrees as radians, squared
const MAX_SLERP_LIMIT = 0.06853891945200942; // 15 degrees as radians, squared

const SLERP_FACTOR = MAX_SLERP / MAX_SLERP_LIMIT;


const vForce = new Cannon.Vec3(0,0,0);
const vForceAt = new Cannon.Vec3(0,0,0);
const vZero = new Cannon.Vec3(0,0,0);
const vThrustForce = new Cannon.Vec3(0,0,0);

//const CollisionShapeExport = require("../docs/blender/collision.json");

// from blender model dimensions
const SHIP_WIDTH = 1.369;
const SHIP_DEPTH = 0.264;
const SHIP_HEIGHT = 1.187;

export const HOVER_HEIGHT = 0.9;

const modelRotation = new Quaternion();
modelRotation.setFromAxisAngle(new Vector3(1,0,0), TAU/4);
const current = new Quaternion();
const yRotation = new Quaternion();
const zRotation = new Quaternion();

const modelUp = new Vector3(0,1,0);
const currentUp = new Vector3(0,0,1);

const tmpPlane = new Plane();

const _DEBUG_ORIENTATION = false;
const GRID_WIDTH = 0.015;

const axisMaterials = [
    new LineBasicMaterial({ color: 0xff00ff }),
    new LineBasicMaterial({ color: 0xffff00 }),
    new LineBasicMaterial({ color: 0x00ffff })
];

export default class XRacer
{
    constructor(position, controls, ctx)
    {

        if (_DEBUG_ORIENTATION)
        {

            this.dbgXAxis = new Mesh(
                new BoxGeometry(
                    2,
                    GRID_WIDTH,
                    GRID_WIDTH
                ),
                axisMaterials[0]
            );

            this.dbgYAxis = new Mesh(
                new BoxGeometry(
                    GRID_WIDTH,
                    2,
                    GRID_WIDTH
                ),
                axisMaterials[1]
            );
            this.dbgZAxis = new Mesh(
                new BoxGeometry(
                    GRID_WIDTH,
                    GRID_WIDTH,
                    2
                ),
                axisMaterials[2]
            );

            ctx.scene.add(this.dbgXAxis);
            ctx.scene.add(this.dbgYAxis);
            ctx.scene.add(this.dbgZAxis);
        }


        this.ctx = ctx;
        this.type = "XRacer";
        this.controls = controls;

        const model = new Mesh(geometry, materials);

        model.position.copy(position);

        model.quaternion.setFromAxisAngle(new Cannon.Vec3(1,0,0), TAU/4);

        this.model = model;
        ctx.scene.add(model);

        //simple cube shape
        const shape = new Cannon.Box(new Cannon.Vec3(
            SHIP_WIDTH/2,
            SHIP_DEPTH/2,
            SHIP_HEIGHT/2
        ));
        const body = new Cannon.Body({
            mass: 50,
            linearDamping: 0.7,
            angularDamping: 0.4,
            material: ctx.defaultMaterial
        });

        body.position.copy(model.position);
        body.quaternion.copy(model.quaternion);

        //body.velocity.set(0,6,0);

        body.addShape(shape);

        this.body = body;

        // frame within the track
        this.frame = 0;
        this.thrustPower = 0;

        this.zRot = 0;// TAU/4;

        ctx.world.addBody(body);

        ctx.world.addEventListener("postStep", this.localGravity);


    }

    destroy()
    {

        this.ctx.scene.remove(this.model);
    }

    getPosition()
    {
        return this.model.position;
    }

    setPosition(x,y,z)
    {

        this.thrustPower = 0;

        const { model } = this;

        model.position.x = x;
        model.position.y = y;
        model.position.z = z;
    }

    first  = true;

    localGravity = () =>
    {
        this.frame = this.adjustFrame();

        const  { ctx, model, frame, body, controls, thrustPower } = this;

        const thrust = controls.getThrust();
        if (thrust)
        {
            if (abs(thrustPower) < 1000)
            {
                this.thrustPower += 100 * thrust;
            }
        }
        else
        {
            if (thrustPower !== 0)
            {
                this.thrustPower *= 0.95;
            }
        }

        this.zRot -= controls.getSteerDirection() * 0.01;

        //Stats.update("thrust", Math.round(this.thrustPower * 10) / 10);

        const engineOff = controls.isEngineOff();
        if (this.thrustPower !== 0 && !engineOff)
        {
            vThrustForce.x = 0;
            vThrustForce.y = 0;
            vThrustForce.z = -THRUST * this.thrustPower;

            body.applyLocalForce(vThrustForce, vZero);
        }

        const track = ctx.track;

        const center = track.positions[frame];

        // the cartesian x,y and z axis as unit vector
        const xAxis = track.xAxes[frame];
        const yAxis = track.yAxes[frame];
        const zAxis = track.zAxes[frame];
        const segment = track.data[track.segments[frame]];

        const { arc , radius } = segment;

        const name = "HV", offX = 0, offY = 0, offZ = 0;

        const pos = model.position;

        // set up local XZ plane
        tmpPlane.setFromNormalAndCoplanarPoint(yAxis.clone(), center.clone());

        // project current pos on local XZ plane
        const projected = tmpPlane.projectPoint(pos);

        const distanceToPlane = tmpPlane.distanceToPoint(pos);


        let quat;
        let zCorrect;
        if (distanceToPlane < 0 && frame > 0)
        {
            const length = track.vSteps[frame - 1];
            const t = -distanceToPlane/length;
            quat = track.quats[frame].clone().slerp(track.quats[frame - 1], t);
            zCorrect = track.zCorrect[frame - 1] * (1 - t) + track.zCorrect[frame] * t;

        }
        else if (distanceToPlane > 0 && frame < track.length - 1)
        {
            const length = track.vSteps[frame];
            const t = distanceToPlane/length;
            quat = track.quats[frame].clone().slerp(track.quats[frame + 1], t);
            zCorrect = track.zCorrect[frame] * (1 - t) + track.zCorrect[frame + 1] * t;
        }
        else
        {
            quat = track.quats[frame];
            zCorrect = track.zCorrect[frame];
        }

        //Stats.update("dP", Math.round(distanceToPlane * 100)/100);

        const projectedToCenter = center.clone().sub(projected);
        const distance = projectedToCenter.length();

        let isFalling = false;
        const angle = projectedToCenter.clone().angleTo(xAxis);
        if (angle < TAU / 4 - arc / 2 || angle > TAU / 4 + arc / 2)
        {
            Stats.update(name, "out of arc");
            isFalling = true;
        }

        if (distance > radius)
        {
            Stats.update(name, "out of radius");
            isFalling = true;
        }

        if (frame === 0 || frame === track.quats.length - 1)
        {
            const centerToPos = pos.clone().sub(center);
            if (centerToPos.length()  > radius * 2)
            {
                Stats.update(name, "off track end");
            }
            isFalling = true;
        }

        if (isFalling || engineOff)
        {
            body.force.x = 0;
            body.force.y = 0;
            body.force.z = -9 * body.mass;

            this.thrustPower  = 0;
        }
        else
        {
            Stats.update(name, "hover");

            const unit = projectedToCenter.clone().normalize();

            const offset = (radius - HOVER_HEIGHT) - distance;
            const force = -1000 * offset; //- DAMPING * u.dot(unit);

            const forceVector = unit.clone().multiplyScalar(force);

            vForce.copy(forceVector);
            vForceAt.copy(pos);
            body.applyForce(vForce, vForceAt);

            //Stats.update("zCorrect", Math.round(zCorrect * 360 / TAU * 10)/10);

            yRotation.setFromAxisAngle(zAxis, angle - TAU / 4);
            zRotation.setFromAxisAngle(yAxis, this.zRot + zCorrect);

            const angleOffset = currentUp.copy(modelUp).applyQuaternion(body.quaternion).angleTo(unit);
            //Stats.update("dA", round(angleOffset * 3600 / TAU) / 10);



            const qTarget = quat.clone().multiply(modelRotation).multiply(yRotation).multiply(zRotation);

            const t = 0.1 + Math.min(angleOffset*angleOffset,MAX_SLERP_LIMIT) * SLERP_FACTOR;

            //Stats.update("slerp", t);

            current.copy(body.quaternion).slerp(qTarget, t);

            body.quaternion.copy(current);

        }
        if (_DEBUG_ORIENTATION)
        {
            this.dbgXAxis.quaternion.copy(quat);
            this.dbgYAxis.quaternion.copy(quat);
            this.dbgZAxis.quaternion.copy(quat);
        }

        materials[THRUST_MAT_INDEX].opacity = (0.6 + Math.random() * 0.11) * max(this.thrustPower,0) / 1000;
    };

    /**
     * Checks if the neighbors of the current frame are better match than the current frame returns their index if so.
     */
    adjustFrame()
    {
        const  { ctx, model, frame } = this;

        const positions = ctx.track.positions;

        const position = model.position.clone();

        const distance = position.distanceTo(positions[frame]);

        if (frame > 0)
        {
            const prevDist = position.distanceTo(positions[frame - 1]);
            if (prevDist < distance)
            {
                return frame - 1;
            }
        }

        if (frame < positions.length - 1)
        {
            const nextDist = position.distanceTo(positions[frame + 1]);
            if (nextDist < distance)
            {
                return frame + 1;
            }
        }
        return frame;
    }


    sync()
    {
        const { ctx, body, model, ctrl } = this;

        const position = body.position;
        const quaternion = body.quaternion;
        model.position.copy(position);
        model.quaternion.copy(quaternion);

        if (_DEBUG_ORIENTATION)
        {
            this.dbgXAxis.position.copy(body.position);
            this.dbgYAxis.position.copy(body.position);
            this.dbgZAxis.position.copy(body.position);
        }

    }

    static load(loader)
    {
        return Promise.all([
            callback(done => loader.load("blender/ship3.json", done)),
            callback(done => loader.load("blender/collision.json", done))
        ]).then(data => {

            [geometry, materials] = data[0];
            [collisionGeom] = data[1];

            // cover
            materials[2] = new MeshLambertMaterial({
                color: 0xff8659,
                transparent: true,
                opacity: 0.5,
                combine: AddOperation
            });

            // thrust
            materials[THRUST_MAT_INDEX] = new MeshLambertMaterial({
                color: 0xff9b2a,
                transparent: true,
                opacity: 0
            });
        });
    }
}

export const SHIP_MATERIAL = new Cannon.Material("SHIP");



// WEBPACK FOOTER //
// ./src/x-racer.js